import React from "react";

/**
 * Vector art for the auth stages, authored inline in the same manner as
 * landingArt.jsx: it stays crisp at any size, carries no network payload or
 * licence, and picks up the brand palette directly. Drawn portrait so it fits
 * the tall left-hand panel, and light-on-dark so it reads against that panel's
 * deep ground.
 */

const C = {
  surface: "#ffffff",
  soft: "#f4f6fa",
  line: "#e3e7ef",
  lineSoft: "#eef1f6",
  muted: "#aab1c0",
  green: "#00ac56",
  greenSoft: "#e8f7ef",
  blue: "#0e5fc1",
  blueSoft: "#eaf1fd",
  violet: "#4a3aa7",
  violetSoft: "#f1eefc",
};

/**
 * Motion lives inside the <svg> as a scoped <style>, exactly as landingArt.jsx
 * does it — an authored SVG can honour prefers-reduced-motion, which a GIF
 * cannot, and weighs a fraction as much.
 */
const MOTION_CSS = `
  @keyframes lgaRise  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
  @keyframes lgaDraw  { to { stroke-dashoffset: 0; } }
  @keyframes lgaCarry {
    0%, 10%   { transform: translate(0, 0); }
    42%, 66%  { transform: translate(280px, 0); }
    94%, 100% { transform: translate(0, 0); }
  }
  @keyframes lgaPulse { 0%, 100% { opacity: .4; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }
  @keyframes lgaRing  { 0% { opacity: .5; transform: scale(.86); } 70%, 100% { opacity: 0; transform: scale(1.3); } }
  @keyframes lgaSheen { 0% { transform: translateX(-140%); } 55%, 100% { transform: translateX(420%); } }
  @keyframes lgaFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes lgaBlink { 0%, 100% { opacity: .2; } 50% { opacity: 1; } }
  @keyframes lgaFill  { 0%, 8% { opacity: 0; transform: translateY(6px); } 22%, 92% { opacity: 1; transform: none; } 100% { opacity: 0; transform: translateY(6px); } }

  .lga-rise  { animation: lgaRise .7s cubic-bezier(.2,.8,.2,1) both; }
  .lga-carry { animation: lgaCarry 7.5s cubic-bezier(.6,0,.35,1) infinite; }
  .lga-sheen { animation: lgaSheen 6s cubic-bezier(.4,0,.2,1) infinite; }
  .lga-float { animation: lgaFloat 6s ease-in-out infinite; }
  .lga-draw  { stroke-dasharray: 340; stroke-dashoffset: 340; animation: lgaDraw 2.2s cubic-bezier(.4,0,.2,1) .5s forwards; }
  .lga-check { stroke-dasharray: 46; stroke-dashoffset: 46; animation: lgaDraw .7s cubic-bezier(.2,.8,.2,1) 1.1s forwards; }
  .lga-pulse { transform-box: fill-box; transform-origin: center; animation: lgaPulse 2.6s ease-in-out infinite; }
  .lga-ring  { transform-box: fill-box; transform-origin: center; animation: lgaRing 2.6s ease-out infinite; }
  .lga-blink { animation: lgaBlink 1.3s ease-in-out infinite; }
  .lga-fill  { animation: lgaFill 6s cubic-bezier(.2,.8,.2,1) infinite; }

  @media (prefers-reduced-motion: reduce) {
    .lga-rise, .lga-carry, .lga-sheen, .lga-float,
    .lga-draw, .lga-check, .lga-pulse, .lga-ring,
    .lga-blink, .lga-fill { animation: none !important; }
    .lga-draw, .lga-check { stroke-dashoffset: 0; }
    .lga-ring { opacity: 0; }
  }
`;

/** One board card — the unit the whole board is built from. */
const TaskCard = ({ x, y, tint, className = "", delay = 0 }) => (
  <g className={`lga-rise ${className}`} style={{ animationDelay: `${delay}s` }}>
    <g filter="url(#lgCardShadow)">
      <rect x={x} y={y} width="120" height="52" rx="13" fill={C.surface} stroke={C.line} />
    </g>
    <rect x={x + 14} y={y + 12} width="72" height="7" rx="3.5" fill={C.line} />
    <rect x={x + 14} y={y + 24} width="48" height="6" rx="3" fill={C.lineSoft} />
    <rect x={x + 14} y={y + 36} width="30" height="9" rx="4.5" fill={tint} />
    <circle cx={x + 101} cy={y + 40} r="8" fill={tint} stroke={C.surface} strokeWidth="2" />
  </g>
);

/** Sprint board with a card travelling to Done, over a burn-down and a status tile. */
export const AuthBoardArt = () => (
  <svg viewBox="0 0 520 540" role="img" aria-label="A Projam sprint board with a task moving into Done">
    <style>{MOTION_CSS}</style>
    <defs>
      <filter id="lgCardShadow" x="-20%" y="-25%" width="140%" height="170%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.10" />
      </filter>
      <filter id="lgPanelShadow" x="-15%" y="-20%" width="130%" height="150%">
        <feDropShadow dx="0" dy="14" stdDeviation="16" floodColor="#020a14" floodOpacity="0.42" />
      </filter>
      <linearGradient id="lgArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={C.green} stopOpacity="0.26" />
        <stop offset="100%" stopColor={C.green} stopOpacity="0" />
      </linearGradient>
      <linearGradient id="lgSheen" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="50%" stopColor="#ffffff" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <clipPath id="lgBoardClip"><rect x="40" y="40" width="440" height="320" rx="20" /></clipPath>
    </defs>

    {/* ------------------------------------------------------------- board */}
    <g filter="url(#lgPanelShadow)">
      <rect x="40" y="40" width="440" height="320" rx="20" fill={C.surface} />
    </g>

    <rect x="64" y="68" width="122" height="12" rx="6" fill={C.line} className="lga-rise" />
    <rect x="64" y="88" width="76" height="8" rx="4" fill={C.lineSoft}
      className="lga-rise" style={{ animationDelay: ".08s" }} />

    {[C.greenSoft, C.blueSoft, C.violetSoft].map((fill, i) => (
      <circle key={fill} cx={396 + i * 24} cy="80" r="12" fill={fill}
        stroke={C.surface} strokeWidth="2.5"
        className="lga-rise" style={{ animationDelay: `${0.16 + i * 0.07}s` }} />
    ))}

    <line x1="40" y1="116" x2="480" y2="116" stroke={C.lineSoft} />

    {[
      { x: 60, tint: C.muted, soft: C.soft },
      { x: 200, tint: C.blue, soft: C.blueSoft },
      { x: 340, tint: C.green, soft: C.greenSoft },
    ].map((col, i) => (
      <g key={col.x} className="lga-rise" style={{ animationDelay: `${0.2 + i * 0.09}s` }}>
        <rect x={col.x} y="134" width="120" height="24" rx="12" fill={col.soft} />
        <circle cx={col.x + 16} cy="146" r="4.5" fill={col.tint} />
        <rect x={col.x + 28} y="142" width="52" height="8" rx="4" fill={col.tint} opacity="0.42" />
      </g>
    ))}

    {/* the slot the travelling card is headed for */}
    <rect x="340" y="172" width="120" height="52" rx="13" fill="none"
      stroke={C.line} strokeDasharray="5 5" />

    <TaskCard x={60} y={234} tint={C.soft} delay={0.34} />
    <TaskCard x={60} y={296} tint={C.soft} delay={0.42} />
    <TaskCard x={200} y={172} tint={C.blueSoft} delay={0.5} />
    <TaskCard x={200} y={234} tint={C.blueSoft} delay={0.58} />
    <TaskCard x={340} y={234} tint={C.greenSoft} delay={0.66} />
    <TaskCard x={340} y={296} tint={C.greenSoft} delay={0.74} />

    {/* To Do → Done, on a loop */}
    <TaskCard x={60} y={172} tint={C.violetSoft} delay={0.26} className="lga-carry" />

    <g clipPath="url(#lgBoardClip)">
      <rect x="40" y="40" width="110" height="320" fill="url(#lgSheen)" className="lga-sheen" />
    </g>

    {/* --------------------------------------------------------- burn-down */}
    <g className="lga-float">
      <g filter="url(#lgPanelShadow)">
        <rect x="40" y="380" width="250" height="124" rx="18" fill={C.surface} />
      </g>
      <rect x="62" y="402" width="86" height="9" rx="4.5" fill={C.line} />
      <rect x="62" y="419" width="52" height="7" rx="3.5" fill={C.lineSoft} />

      <rect x="208" y="399" width="60" height="22" rx="11" fill={C.greenSoft} />
      <circle cx="222" cy="410" r="4" fill={C.green} className="lga-pulse" />
      <rect x="232" y="406" width="26" height="8" rx="4" fill={C.green} opacity="0.45" />

      {[452, 470].map((y) => (
        <line key={y} x1="62" y1={y} x2="268" y2={y} stroke={C.lineSoft} strokeDasharray="4 5" />
      ))}
      <line x1="62" y1="486" x2="268" y2="486" stroke={C.line} />

      <path d="M62,442 L96,450 L130,446 L164,460 L198,470 L232,476 L268,482 L268,486 L62,486 Z"
        fill="url(#lgArea)" />
      <polyline points="62,442 96,450 130,446 164,460 198,470 232,476 268,482"
        fill="none" stroke={C.green} strokeWidth="3"
        strokeLinecap="round" strokeLinejoin="round" className="lga-draw" />

      {[[62, 442], [130, 446], [198, 470], [268, 482]].map(([cx, cy], i) => (
        <circle key={cx} cx={cx} cy={cy} r="4.5" fill={C.green} stroke={C.surface} strokeWidth="2.5"
          className="lga-rise" style={{ animationDelay: `${0.8 + i * 0.18}s` }} />
      ))}
    </g>

    {/* ------------------------------------------------------- sprint done */}
    <g className="lga-float" style={{ animationDuration: "7.5s", animationDelay: "-2s" }}>
      <g filter="url(#lgPanelShadow)">
        <rect x="306" y="380" width="174" height="124" rx="18" fill={C.surface} />
      </g>
      <rect x="328" y="402" width="72" height="9" rx="4.5" fill={C.line} />
      <rect x="328" y="419" width="44" height="7" rx="3.5" fill={C.lineSoft} />

      <circle cx="393" cy="463" r="28" fill={C.greenSoft} />
      <circle cx="393" cy="463" r="28" fill="none" stroke={C.green} strokeWidth="2" className="lga-ring" />
      <path d="M381,463 l8.5,8.5 L406,455" fill="none" stroke={C.green} strokeWidth="4"
        strokeLinecap="round" strokeLinejoin="round" className="lga-check" />
    </g>
  </svg>
);

/** Code-entry: the one-time code the reset flow mails out, filling in. */
export const AuthSecureArt = () => (
  <svg viewBox="0 0 520 540" role="img" aria-label="A one-time code being entered and verified">
    <style>{MOTION_CSS}</style>
    <defs>
      <filter id="lgSecPanel" x="-15%" y="-20%" width="130%" height="150%">
        <feDropShadow dx="0" dy="14" stdDeviation="16" floodColor="#020a14" floodOpacity="0.42" />
      </filter>
      <filter id="lgSecCard" x="-20%" y="-25%" width="140%" height="170%">
        <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#0f172a" floodOpacity="0.10" />
      </filter>
      <linearGradient id="lgSecSheen" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="50%" stopColor="#ffffff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </linearGradient>
      <clipPath id="lgSecPill"><rect x="64" y="268" width="168" height="40" rx="20" /></clipPath>
    </defs>

    {/* ------------------------------------------------------------ panel */}
    <g filter="url(#lgSecPanel)">
      <rect x="40" y="60" width="440" height="300" rx="20" fill={C.surface} />
    </g>

    {/* the mail the code arrived in */}
    <g className="lga-rise">
      <rect x="64" y="88" width="46" height="36" rx="11" fill={C.greenSoft} />
      <path d="M74,98 h26 a3,3 0 0 1 3,3 v14 a3,3 0 0 1 -3,3 h-26 a3,3 0 0 1 -3,-3 v-14 a3,3 0 0 1 3,-3 z"
        fill="none" stroke={C.green} strokeWidth="2" />
      <path d="M71,101 l16,10 l16,-10" fill="none" stroke={C.green} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <rect x="124" y="94" width="132" height="11" rx="5.5" fill={C.line}
      className="lga-rise" style={{ animationDelay: ".08s" }} />
    <rect x="124" y="113" width="86" height="8" rx="4" fill={C.lineSoft}
      className="lga-rise" style={{ animationDelay: ".14s" }} />

    <line x1="40" y1="150" x2="480" y2="150" stroke={C.lineSoft} />

    {/* the six code boxes, filling one after another on a loop */}
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const x = 64 + i * 66;
      return (
        <g key={x} className="lga-rise" style={{ animationDelay: `${0.2 + i * 0.06}s` }}>
          <rect x={x} y="180" width="52" height="62" rx="13"
            fill={i < 4 ? C.surface : C.soft} stroke={i < 4 ? C.green : C.line}
            strokeOpacity={i < 4 ? 0.45 : 1} />
          {i < 4 ? (
            <rect x={x + 17} y="204" width="18" height="14" rx="7" fill={C.green}
              className="lga-fill" style={{ animationDelay: `${i * 0.32}s` }} />
          ) : null}
          {i === 4 ? (
            <rect x={x + 25} y="198" width="2.5" height="26" rx="1.25" fill={C.muted}
              className="lga-blink" />
          ) : null}
        </g>
      );
    })}

    {/* verify action */}
    <g className="lga-rise" style={{ animationDelay: ".6s" }}>
      <rect x="64" y="268" width="168" height="40" rx="20" fill={C.green} />
      <rect x="96" y="284" width="104" height="9" rx="4.5" fill="#ffffff" opacity="0.9" />
      <g clipPath="url(#lgSecPill)">
        <rect x="64" y="268" width="60" height="40" fill="url(#lgSecSheen)" className="lga-sheen" />
      </g>
    </g>
    <rect x="252" y="283" width="96" height="10" rx="5" fill={C.lineSoft}
      className="lga-rise" style={{ animationDelay: ".68s" }} />

    {/* --------------------------------------------------------- shielded */}
    <g className="lga-float">
      <g filter="url(#lgSecPanel)">
        <rect x="40" y="380" width="250" height="124" rx="18" fill={C.surface} />
      </g>
      <rect x="62" y="402" width="86" height="9" rx="4.5" fill={C.line} />
      <rect x="62" y="419" width="52" height="7" rx="3.5" fill={C.lineSoft} />

      <g filter="url(#lgSecCard)">
        <path d="M105,442 l34,13 v20 c0,18 -14,29 -34,35 c-20,-6 -34,-17 -34,-35 v-20 z"
          fill={C.greenSoft} stroke={C.green} strokeOpacity="0.35" strokeWidth="1.5" />
      </g>
      <path d="M93,478 l8,8 l19,-19" fill="none" stroke={C.green} strokeWidth="4"
        strokeLinecap="round" strokeLinejoin="round" className="lga-check" />

      <rect x="160" y="452" width="106" height="9" rx="4.5" fill={C.line} />
      <rect x="160" y="470" width="76" height="8" rx="4" fill={C.lineSoft} />
      <circle cx="166" cy="494" r="4" fill={C.green} className="lga-pulse" />
      <rect x="178" y="490" width="58" height="8" rx="4" fill={C.green} opacity="0.4" />
    </g>

    {/* -------------------------------------------------------------- key */}
    <g className="lga-float" style={{ animationDuration: "7.5s", animationDelay: "-2s" }}>
      <g filter="url(#lgSecPanel)">
        <rect x="306" y="380" width="174" height="124" rx="18" fill={C.surface} />
      </g>
      <rect x="328" y="402" width="72" height="9" rx="4.5" fill={C.line} />
      <rect x="328" y="419" width="44" height="7" rx="3.5" fill={C.lineSoft} />

      <circle cx="393" cy="463" r="28" fill={C.blueSoft} />
      <circle cx="393" cy="463" r="28" fill="none" stroke={C.blue} strokeWidth="2" className="lga-ring" />
      <path d="M386,459 v-5 a7,7 0 0 1 14,0 v5" fill="none" stroke={C.blue} strokeWidth="3"
        strokeLinecap="round" />
      <rect x="381" y="459" width="24" height="18" rx="5" fill={C.blue} opacity="0.85" />
    </g>
  </svg>
);

export default AuthBoardArt;
